// Gouvernance — Les 4 Gardiens (Groot, Gamora, Gatekeeper, Nebula).
// Bloc unique parce que les 4 cartes forment une vue cohérente.
// (En V2 on pourra les éclater en 4 widgets séparés si JJ veut activer
// individuellement.)
import {
  Shield, Database, GitBranch, Activity, XCircle, AlertTriangle,
  Ticket, Eye, FlaskConical, Loader2,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { useGovernanceData } from "./useGovernanceFetch";

export default function GouvGardiensWidget() {
  const { data, loading } = useGovernanceData();
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
      </div>
    );
  }

  const gk = data.gatekeeper;
  const gamora = data.gamora;
  const nebula = data.nebula;

  const grootCritical = gk?.latest_scan?.violations_critical?.length ?? gk?.latest_scan?.violations_critical ?? 0;
  const grootHigh = gk?.latest_scan?.violations_high?.length ?? gk?.latest_scan?.violations_high ?? 0;
  const grootTickets = gk?.latest_scan?.tickets_created ?? 0;
  const grootHealthy = grootCritical === 0;

  const gamoraChecks = Array.isArray(gamora?.checks) ? gamora.checks : [];
  const gamoraIssues = gamoraChecks.reduce((s, c) => s + (c?.count || 0), 0);
  const gamoraCritique = gamoraChecks.filter((c) => c?.severity === "critique" && (c?.count || 0) > 0).length;
  const gamoraGrave = gamoraChecks.filter((c) => c?.severity === "grave" && (c?.count || 0) > 0).length;
  const gamoraHealthy = gamoraIssues === 0;

  const canDeploy = gk?.latest_scan?.can_deploy;
  const e2eRaw = gk?.latest_scan?.e2e_passed ?? null;
  const e2ePassed = typeof e2eRaw === "boolean" ? (e2eRaw ? 1 : 0) : (typeof e2eRaw === "number" ? e2eRaw : null);
  const e2eTotal = gk?.latest_scan?.e2e_total ?? (typeof e2eRaw === "boolean" ? 1 : null);
  const e2ePercent = e2eRaw === true ? 100 : e2eRaw === false ? 0
    : (typeof e2ePassed === "number" && typeof e2eTotal === "number" && e2eTotal > 0)
      ? Math.round((e2ePassed / e2eTotal) * 100) : null;

  const nebulaScore = typeof nebula?.score_global === "number" ? nebula.score_global : null;
  const nebulaColor = nebula?.health_color || "zinc";

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="gouv-gardiens-widget">
      {/* Groot */}
      <div className={`p-5 rounded-xl border-2 ${grootHealthy ? "bg-zinc-900/50 border-zinc-800" : "bg-red-500/5 border-red-500/40"}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 rounded-lg bg-blue-500/20"><Shield className="w-6 h-6 text-blue-400" /></div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">Groot</h3>
            <p className="text-xs text-blue-400/80">Linter code + Tenant</p>
          </div>
        </div>
        <div className="space-y-2.5">
          <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-400" /><span className="text-sm text-zinc-300">Critiques</span></div>
            <Badge className={grootCritical > 0 ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"}>{grootCritical}</Badge>
          </div>
          <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-sm text-zinc-300">High</span></div>
            <Badge className={grootHigh > 0 ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/20 text-emerald-300"}>{grootHigh}</Badge>
          </div>
          <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-2"><Ticket className="w-4 h-4 text-purple-400" /><span className="text-sm text-zinc-300">Tickets créés</span></div>
            <Badge className="bg-purple-500/20 text-purple-300">{grootTickets}</Badge>
          </div>
        </div>
        <div className="mt-4 p-2 rounded bg-blue-500/10 border border-blue-500/20">
          <p className="text-xs text-blue-300 flex items-center gap-1"><Eye className="w-3 h-3" />Analyse statique du code source</p>
        </div>
      </div>

      {/* Gamora */}
      <div className={`p-5 rounded-xl border-2 ${gamoraHealthy ? "bg-zinc-900/50 border-zinc-800" : gamoraCritique > 0 ? "bg-red-500/5 border-red-500/40" : "bg-amber-500/5 border-amber-500/40"}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 rounded-lg bg-pink-500/20"><Database className="w-6 h-6 text-pink-400" /></div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">Gamora</h3>
            <p className="text-xs text-pink-400/80">Data integrity métier</p>
          </div>
        </div>
        <div className="space-y-2.5">
          <div className="text-center py-2">
            <div className={`text-4xl font-bold ${gamoraHealthy ? "text-emerald-400" : gamoraCritique > 0 ? "text-red-400" : "text-amber-400"}`}>{gamoraIssues}</div>
            <p className="text-xs text-zinc-500 mt-1">issues sur {gamoraChecks.length} checks</p>
          </div>
          <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
            <span className="text-sm text-zinc-300">Critiques</span>
            <Badge className={gamoraCritique > 0 ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"}>{gamoraCritique}</Badge>
          </div>
          <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
            <span className="text-sm text-zinc-300">Graves</span>
            <Badge className={gamoraGrave > 0 ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/20 text-emerald-300"}>{gamoraGrave}</Badge>
          </div>
        </div>
        <div className="mt-4 p-2 rounded bg-pink-500/10 border border-pink-500/20">
          <p className="text-xs text-pink-300 flex items-center gap-1"><Database className="w-3 h-3" />Cohérence MongoDB métier (TEF)</p>
        </div>
      </div>

      {/* Gatekeeper */}
      <div className={`p-5 rounded-xl border-2 ${canDeploy === true ? "bg-emerald-500/5 border-emerald-500/40" : canDeploy === false ? "bg-red-500/5 border-red-500/40" : "bg-zinc-900/50 border-zinc-800"}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2.5 rounded-lg ${canDeploy === true ? "bg-emerald-500/20" : canDeploy === false ? "bg-red-500/20" : "bg-zinc-800"}`}>
            <GitBranch className={`w-6 h-6 ${canDeploy === true ? "text-emerald-400" : canDeploy === false ? "text-red-400" : "text-zinc-400"}`} />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">Gatekeeper</h3>
            <p className={`text-xs ${canDeploy === true ? "text-emerald-400/80" : canDeploy === false ? "text-red-400/80" : "text-zinc-400"}`}>Gate CI/CD pré-deploy</p>
          </div>
        </div>
        <div className="space-y-2.5">
          <div className="text-center py-2">
            <div className={`text-2xl font-bold ${canDeploy === true ? "text-emerald-400" : canDeploy === false ? "text-red-400" : "text-zinc-400"}`}>
              {canDeploy === true ? "DEPLOY OK" : canDeploy === false ? "BLOQUÉ" : "—"}
            </div>
          </div>
          <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-2"><FlaskConical className="w-4 h-4 text-cyan-400" /><span className="text-sm text-zinc-300">Tests E2E</span></div>
            <Badge className={
              e2ePercent === null ? "bg-zinc-700/50 text-zinc-400" :
              e2ePercent >= 80 ? "bg-emerald-500/20 text-emerald-300" :
              e2ePercent >= 50 ? "bg-yellow-500/20 text-yellow-300" : "bg-red-500/20 text-red-300"
            }>{e2ePercent === null ? "—" : e2eRaw === true ? "OK" : `${e2ePassed ?? "—"}/${e2eTotal ?? "—"}`}</Badge>
          </div>
          <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-orange-400" /><span className="text-sm text-zinc-300">Critiques</span></div>
            <Badge className="bg-orange-500/20 text-orange-300">{grootCritical}</Badge>
          </div>
        </div>
        <div className="mt-4 p-2 rounded bg-purple-500/10 border border-purple-500/20">
          <p className="text-xs text-purple-300 flex items-center gap-1"><GitBranch className="w-3 h-3" />Bloque le merge si E2E KO ou critiques</p>
        </div>
      </div>

      {/* Nebula */}
      <div className={`p-5 rounded-xl border-2 ${
        nebulaColor === "green" ? "bg-emerald-500/5 border-emerald-500/40" :
        nebulaColor === "yellow" ? "bg-yellow-500/5 border-yellow-500/40" :
        nebulaColor === "orange" ? "bg-orange-500/5 border-orange-500/40" :
        nebulaColor === "red" ? "bg-red-500/5 border-red-500/40" : "bg-zinc-900/50 border-zinc-800"
      }`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 rounded-lg bg-violet-500/20"><Activity className="w-6 h-6 text-violet-400" /></div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">Nebula</h3>
            <p className="text-xs text-violet-400/80">Monitoring runtime app</p>
          </div>
        </div>
        <div className="space-y-2.5">
          <div className="text-center py-2">
            <div className={`text-4xl font-bold ${
              nebulaColor === "green" ? "text-emerald-400" :
              nebulaColor === "yellow" ? "text-yellow-400" :
              nebulaColor === "orange" ? "text-orange-400" :
              nebulaColor === "red" ? "text-red-400" : "text-zinc-400"
            }`}>{nebulaScore !== null ? `${nebulaScore}%` : "—"}</div>
            <p className="text-xs text-zinc-500 mt-1">Score santé</p>
          </div>
          {nebulaScore !== null && <Progress value={nebulaScore} className="h-2" />}
          <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
            <span className="text-sm text-zinc-300">Checks passés</span>
            <Badge className="bg-violet-500/20 text-violet-300">{nebula?.summary?.passed ?? 0}/{nebula?.summary?.total ?? 0}</Badge>
          </div>
        </div>
        <div className="mt-4 p-2 rounded bg-violet-500/10 border border-violet-500/20">
          <p className="text-xs text-violet-300 flex items-center gap-1"><Eye className="w-3 h-3" />Smoke / Design / Features / Code</p>
        </div>
      </div>
    </div>
  );
}
